import { createContext, useContext, useState, useCallback, useRef, useEffect, type ReactNode } from "react";

const EXPIRATION_MS = 60_000;

interface RevealedCouponsContextType {
  revealOffer: (couponId: string, brandSlug?: string, isStorePage?: boolean) => void;
  isRevealed: (couponId: string, brandSlug?: string) => boolean;
}

const RevealedCouponsContext = createContext<RevealedCouponsContextType | null>(null);

const STORAGE_KEY_IDS = "revealed_coupon_ids";

const getStoredMap = (): Record<string, number> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_IDS);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    // Migration: if old format (array), discard it
    if (Array.isArray(parsed)) {
      localStorage.removeItem(STORAGE_KEY_IDS);
      return {};
    }
    // Filter out expired
    const now = Date.now();
    const valid: Record<string, number> = {};
    for (const [id, ts] of Object.entries(parsed)) {
      if (typeof ts === "number" && now - ts < EXPIRATION_MS) {
        valid[id] = ts;
      }
    }
    return valid;
  } catch {
    return {};
  }
};

export const RevealedCouponsProvider = ({ children }: { children: ReactNode }) => {
  const [revealedMap, setRevealedMap] = useState<Record<string, number>>(getStoredMap);
  const timersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // Schedule timers for initially valid coupons
  useEffect(() => {
    const now = Date.now();
    for (const [id, ts] of Object.entries(revealedMap)) {
      const remaining = EXPIRATION_MS - (now - ts);
      if (remaining > 0 && !timersRef.current[id]) {
        timersRef.current[id] = setTimeout(() => {
          removeCoupon(id);
        }, remaining);
      }
    }
    return () => {
      Object.values(timersRef.current).forEach(clearTimeout);
      timersRef.current = {};
    };
    // Only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeCoupon = (couponId: string) => {
    setRevealedMap((prev) => {
      const next = { ...prev };
      delete next[couponId];
      localStorage.setItem(STORAGE_KEY_IDS, JSON.stringify(next));
      return next;
    });
    delete timersRef.current[couponId];
  };

  const revealOffer = useCallback((couponId: string, _brandSlug?: string, _isStorePage?: boolean) => {
    setRevealedMap((prev) => {
      if (prev[couponId]) return prev;
      const next = { ...prev, [couponId]: Date.now() };
      localStorage.setItem(STORAGE_KEY_IDS, JSON.stringify(next));
      return next;
    });

    // Schedule expiration
    if (!timersRef.current[couponId]) {
      timersRef.current[couponId] = setTimeout(() => {
        removeCoupon(couponId);
      }, EXPIRATION_MS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isRevealed = useCallback((couponId: string, _brandSlug?: string) => {
    const ts = revealedMap[couponId];
    if (!ts) return false;
    return Date.now() - ts < EXPIRATION_MS;
  }, [revealedMap]);

  return (
    <RevealedCouponsContext.Provider value={{ revealOffer, isRevealed }}>
      {children}
    </RevealedCouponsContext.Provider>
  );
};

export const useRevealedCoupons = () => {
  const ctx = useContext(RevealedCouponsContext);
  if (!ctx) throw new Error("useRevealedCoupons must be used within RevealedCouponsProvider");
  return ctx;
};
